import { observer } from 'mobx-react-lite';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

import { useUserStore } from '@/configs/store';
import { UserSelect } from '@/tracker/entities/user';
import { SearchSelect } from '@/tracker/ui/SearchSelect';
import { getId, getName } from '@/tracker/utils/optionGetters';

import { CreateIssueFormValues } from '../CreateIssueForm/types';
import { FormField } from '../FormField';
import { KeySelect } from '../KeySelect';
import { ParentTaskSelect } from '../ParentTaskSelect';
import { SprintSelect } from '../SprintSelect';
import { CreateIssueStore } from '../../model/CreateIssueStore';

import s from './CreateIssueAside.module.scss';

type Props = {
  store: CreateIssueStore;
  control: Control<CreateIssueFormValues>;
  setValue: UseFormSetValue<CreateIssueFormValues>;
};

/** Параметры новой задачи справа, как в Яндекс Трекере: тип, статус, люди, родитель, доска, спринт */
const CreateIssueAside: React.FC<Props> = observer(({ store, control, setValue }) => {
  const { user } = useUserStore();
  // Читаем здесь, а не в render-функциях Controller: они выполняются вне observer
  const { queue, sprints } = store;

  return (
    <aside className={s.aside}>
      <FormField label="Тип">
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <KeySelect
              value={field.value}
              onChange={field.onChange}
              options={queue?.taskTypes ?? null}
              ariaLabel="Тип"
            />
          )}
        />
      </FormField>

      <FormField label="Статус">
        <Controller
          control={control}
          name="status"
          render={({ field }) => (
            <KeySelect
              value={field.value}
              onChange={field.onChange}
              options={queue?.statuses ?? null}
              ariaLabel="Статус"
            />
          )}
        />
      </FormField>

      <FormField label="Исполнитель">
        <Controller
          control={control}
          name="assignee"
          render={({ field }) => (
            <UserSelect
              value={field.value}
              onChange={field.onChange}
              placeholder="Не назначен"
              ariaLabel="Исполнитель"
            />
          )}
        />
        {user && (
          <button
            type="button"
            className={s.aside__link}
            onClick={() => setValue('assignee', { id: user.id, displayName: user.fullName })}
          >
            Назначить меня
          </button>
        )}
      </FormField>

      <FormField label="Родительская задача">
        <Controller
          control={control}
          name="parent"
          render={({ field }) => (
            <ParentTaskSelect
              value={field.value}
              onChange={field.onChange}
              search={store.searchTasks}
            />
          )}
        />
      </FormField>

      <FormField label="Доска">
        <Controller
          control={control}
          name="board"
          render={({ field }) => (
            <SearchSelect
              value={field.value}
              onChange={(board) => {
                field.onChange(board);
                store.selectBoard(board?.id ?? null);
              }}
              search={store.searchBoards}
              getId={getId}
              getLabel={getName}
              ariaLabel="Доска"
              placeholder="Без доски"
            />
          )}
        />
      </FormField>

      <FormField label="Спринт">
        <Controller
          control={control}
          name="sprint"
          render={({ field }) => (
            <SprintSelect
              value={field.value}
              onChange={field.onChange}
              sprints={sprints}
              hasQueue={!!queue}
            />
          )}
        />
      </FormField>
    </aside>
  );
});

export { CreateIssueAside };
